import { forwardRef } from "react";

import cls from "./Button.module.scss";
import { classNames } from "../../lib/classNames/classNames";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { useFlexibleResponsiveValue } from "../../hooks/useResponsiveValue/useResponsiveValue";

/**
 * @typedef {'accent' | 'white'| 'link'| 'light'} ButtonVariant
 */

/**
 * @typedef {Object} ButtonProps
 * @property {string} [className]
 * @property {string} [linkTo]
 * @property {string} [hashLinkTo]
 * @property {ButtonVariant} [variant]
 * @property {React.ReactNode} [children]
 * @property {boolean} [disabled]
 * @property {boolean} [maxWidth]
 * @property {boolean} [round]
 * @property {React.ReactNode} [addonRight]
 * @property {React.ButtonHTMLAttributes<HTMLButtonElement>} [otherProps]
 *  @property {string | Object} [padding]
 *  @property {string | Object} [margin]
 */

/**
 * @type {import('react').ForwardRefExoticComponent<React.PropsWithoutRef<ButtonProps> & React.RefAttributes<HTMLButtonElement>>}
 */
export const Button = forwardRef((props, ref) => {
    const {
        className,
        children,
        variant = "accent",
        disabled,
        maxWidth,
        linkTo,
        hashLinkTo,
        round,
        addonRight,
        padding,
        margin,
        ...otherProps
    } = props;
    const resolvedMaxWidth = useFlexibleResponsiveValue(maxWidth);
    const resolvedPadding = useFlexibleResponsiveValue(padding);
    const resolvedMargin = useFlexibleResponsiveValue(margin);

    const mods = {
        [cls.disabled]: disabled,
        [cls.maxWidth]: resolvedMaxWidth,
        [cls.round]: round,
    };

    const addClass = [className, cls[variant]];

    if (linkTo) {
        return (
            <Link
                to={linkTo}
                style={{ padding: resolvedPadding }}
                className={classNames(cls.button, mods, addClass)}
                {...otherProps}
            >
                {children}
            </Link>
        );
    }
    if (hashLinkTo) {
        return (
            <HashLink
                smooth
                to={hashLinkTo}
                style={{ padding: resolvedPadding }}
                className={classNames(cls.button, mods, addClass)}
                {...otherProps}
            >
                {children}
            </HashLink>
        );
    }

    return (
        <button
            type="button"
            disabled={disabled}
            style={{ padding: resolvedPadding, margin: resolvedMargin }}
            className={classNames(cls.button, mods, addClass)}
            {...otherProps}
            ref={ref}
        >
            {children}
            {addonRight && <div className={cls.addonRight}>{addonRight}</div>}
        </button>
    );
});
